import {
  Directive,
  ElementRef,
  Input,
  OnInit,
  OnChanges,
  type SimpleChanges,
  Renderer2
} from '@angular/core';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'danger'
  | 'link'
  | 'ghost'
  | 'fancy'
  | 'transparent'
  | 'small'
  | 'small-no'
  | 'large'
  | 'large-search';

@Directive({
  selector: '[appButton]',
  standalone: false
})
export class ButtonDirective implements OnInit, OnChanges {
  @Input() appButton: ButtonVariant = 'primary';
  @Input() loading = false;
  @Input() iconOnly = false;

  private previousClasses: string[] = [];

  constructor(
    private el: ElementRef<HTMLElement>,
    private renderer: Renderer2
  ) {}

  ngOnInit(): void {
    this.updateClasses();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['appButton'] || changes['loading'] || changes['iconOnly']) {
      this.updateClasses();
    }
  }

  private updateClasses(): void {
    // Remove previous classes
    this.previousClasses.forEach((cls) => {
      this.renderer.removeClass(this.el.nativeElement, cls);
    });

    // Calculate new classes
    const classes = this.getClasses();

    // Add new classes
    classes.forEach((cls) => {
      this.renderer.addClass(this.el.nativeElement, cls);
    });

    // Store for next update
    this.previousClasses = classes;

    // Handle loading state
    if (this.loading) {
      this.renderer.setAttribute(this.el.nativeElement, 'disabled', 'true');
      this.renderer.setAttribute(this.el.nativeElement, 'aria-busy', 'true');
    } else {
      this.renderer.removeAttribute(this.el.nativeElement, 'aria-busy');
    }
  }

  private getClasses(): string[] {
    const classes: string[] = [];

    switch (this.appButton) {
      case 'small':
        classes.push('small-button');
        break;
      case 'small-no':
        classes.push('small-button', 'small-button--no');
        break;
      case 'large':
        classes.push('large-button');
        break;
      case 'large-search':
        classes.push('large-button', 'large-button--search');
        break;
      case 'primary':
        classes.push('light-btn', 'light-btn-primary');
        break;
      case 'secondary':
        classes.push('light-btn', 'light-btn-secondary');
        break;
      case 'danger':
        classes.push('light-btn', 'light-btn-danger');
        break;
      case 'link':
        classes.push('light-btn', 'light-btn-link');
        break;
      case 'ghost':
        classes.push('light-btn', 'light-btn-ghost');
        break;
      case 'fancy':
        classes.push('light-btn', 'light-btn-fancy');
        break;
      case 'transparent':
        classes.push('light-btn', 'light-btn-transparent');
        break;
    }

    if (this.loading) {
      classes.push('light-btn-loading');
    }

    if (this.iconOnly && classes.includes('light-btn')) {
      classes.push('light-btn-ico');
    }

    return classes;
  }
}
